import { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import { useSEO, faqLd, breadcrumbLd } from "@/lib/seo";
import BookButton from "@/components/BookButton";
import SocialProof from "@/components/SocialProof";
import { PROPERTY } from "@/lib/constants";

export default function FAQ() {
  const [faqs, setFaqs] = useState([]);
  const [filter, setFilter] = useState("All");
  const [open, setOpen] = useState(null);

  useEffect(() => {
    api.get("/faqs").then((r) => setFaqs(r.data)).catch(() => {});
  }, []);

  const categories = useMemo(() => {
    const set = new Set(faqs.map((f) => f.category));
    return ["All", ...Array.from(set)];
  }, [faqs]);

  const visible = filter === "All" ? faqs : faqs.filter((f) => f.category === filter);

  useSEO({
    title: "Frequently Asked Questions — Westbridge Inn & Suites",
    description:
      "Direct answers about Westbridge Inn & Suites in Carrollton, MO: location, pet policy, truck parking, ADA rooms, check-in times, group rates and more.",
    path: "/faq",
    jsonLd: [
      faqLd(faqs),
      breadcrumbLd([
        { name: "Home", path: "/" },
        { name: "FAQ", path: "/faq" },
      ]),
    ],
  });

  return (
    <div data-testid="faq-page">
      <section className="pt-36 pb-12 bg-canvas">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-8">
            <p className="font-mono-label text-ember mb-4">Frequently asked questions</p>
            <h1 className="font-display text-6xl md:text-7xl text-ink leading-[0.98]">
              The answers<br /><span className="italic">people actually need.</span>
            </h1>
          </div>
          <div className="md:col-span-4">
            <p className="text-ink-soft leading-relaxed">
              {faqs.length > 0 ? `${faqs.length} direct answers` : "Direct answers"} across every category —
              location, policies, pets, trucks, family travel, nearby towns, local food, community,
              the road, and the hotel — written so search engines and people both get straight answers.
            </p>
          </div>
        </div>
      </section>

      <section className="bg-canvas pb-12">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 flex flex-wrap gap-2" data-testid="faq-categories">
          {categories.map((c) => (
            <button
              key={c}
              onClick={() => { setFilter(c); setOpen(null); }}
              className={`font-mono-label px-4 py-2 transition-colors ${filter === c ? "bg-ink text-canvas" : "bg-sand text-ink-soft hover:bg-dune"}`}
              data-testid={`faq-cat-${c.toLowerCase()}`}
            >
              {c}
            </button>
          ))}
        </div>
      </section>

      <section className="bg-canvas pb-24">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-12">
          <div className="md:col-span-8">
            <ul className="divide-y divide-rule-subtle border-y border-rule-strong">
              {visible.map((f, i) => {
                const isOpen = open === f.id;
                return (
                  <li key={f.id} data-testid={`faq-${i}`}>
                    <button
                      onClick={() => setOpen(isOpen ? null : f.id)}
                      className="w-full text-left py-6 flex justify-between items-start gap-6 group"
                      aria-expanded={isOpen}
                      data-testid={`faq-toggle-${i}`}
                    >
                      <span className="font-display text-xl md:text-2xl text-ink leading-tight pr-8">{f.question}</span>
                      <span className={`text-ember text-3xl leading-none transition-transform shrink-0 ${isOpen ? "rotate-45" : ""}`} aria-hidden="true">+</span>
                    </button>
                    {isOpen && (
                      <div className="pb-6 -mt-2 text-ink-soft leading-relaxed text-lg max-w-3xl fade-up" data-testid={`faq-answer-${i}`}>
                        {f.answer}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>

          <aside className="md:col-span-4 md:sticky md:top-32 self-start">
            <div className="bg-sand p-8 md:p-10">
              <p className="font-mono-label text-ember mb-3">Didn't see your question?</p>
              <p className="font-display text-3xl text-ink leading-tight">
                Call the front desk — we're up.
              </p>
              <a href={`tel:${PROPERTY.phone.replace(/[^\d+]/g, "")}`} className="mt-6 block font-display text-2xl text-ember hover:text-ember-hover" data-testid="faq-aside-phone">
                {PROPERTY.phone}
              </a>
              <p className="mt-2 font-mono-label text-ink-muted">Staffed 24 hours</p>
              <div className="mt-6">
                <BookButton label="Book a Room" testid="faq-aside-book" />
              </div>
            </div>
            <div className="mt-8">
              <SocialProof variant="compact" />
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}
